import type { ComponentType } from 'react';

export interface FedPrinciple {
  en: string;
  body: string;
}

export const FED_PRINCIPLE: FedPrinciple = {
  en: 'Independent, but federated.',
  body:
    "Mystar Engineering's group-management policy: each group company runs its own business autonomously, while cooperating across technology, recruitment, training, finance, sales and procurement. The federation is what lets independent companies access scale without losing identity.",
};

// Icon components — imported in components/federation/icons.tsx
export interface FedTrait {
  n: string;
  icon: 'growing' | 'autonomy' | 'support';
  title: string;
  body: string;
}

export const FED_TRAITS: FedTrait[] = [
  {
    n: '01',
    icon: 'growing',
    title: 'Growing together',
    body:
      "Each group company pursues its own business growth, while operating together as a Technical Services Federation under a shared mission — protecting Japan's infrastructure and manufacturing industries.",
  },
  {
    n: '02',
    icon: 'autonomy',
    title: 'Autonomy preserved',
    body:
      "Not a top-down parent-subsidiary relationship. The group respects each company's technical strength and brand, and delegates autonomous management of its own business domain.",
  },
  {
    n: '03',
    icon: 'support',
    title: 'Track-record-backed support',
    body:
      'Recruitment, training, accounting, HR, finance, sales, procurement — group-held back-office and operating assets are made available to every federation company, supporting both succession and growth.',
  },
];

export interface FedArchPillar {
  name: string;
  kind: 'anchor' | 'group';
}

export const FED_ARCH = {
  top: {
    label: 'Human Capital Development',
    sub: 'Group-wide recruitment, training and talent mobility',
    flow: ['Recruitment', 'Training', 'Sales cooperation', 'Workforce mobility'],
  },
  pillars: [
    { name: 'Mystar Engineering', kind: 'anchor' },
    { name: 'Group Company',      kind: 'group'  },
    { name: 'Group Company',      kind: 'group'  },
    { name: 'Group Company',      kind: 'group'  },
    { name: 'Group Company',      kind: 'group'  },
  ] as FedArchPillar[],
  mid: "Each group company keeps its own technical strength and operates autonomously in its field.",
  base: {
    label: 'Management Support',
    sub: 'Group-wide consolidation of administrative, sales and procurement functions, raising operating efficiency',
  },
} as const;

// HCD content
export interface HcdCard {
  n: string;
  title: string;
  body: string;
}

export const HCD_RECRUIT: HcdCard[] = [
  { n: 'A', title: 'Agent-network leverage',       body: 'Tap a network of dozens of recruiting agencies — accessible only at group scale, not at single-company scale.' },
  { n: 'B', title: 'Selection & process design',   body: 'Build interview and assessment processes that surface real engineering fit and convert top candidates.' },
  { n: 'C', title: 'Recruitment content production', body: 'Career sites, articles and editorial assets that motivate engineers to choose the federation.' },
];

export interface HiringPoint { y: string; n: number; }

export const HCD_HIRING: HiringPoint[] = [
  { y: '2023', n: 230 },
  { y: '2024', n: 300 },
  { y: '2025', n: 400 },
];

export const HCD_TRAINING: string[] = [
  'New-graduate training',
  'Mid-career hire training',
  'Leadership & management',
  'Electrical engineer development',
  'Design engineer development',
  'Image-processing engineer development',
];

// Succession + back-office
export interface SuccessionCase { n: string; body: string; }
export interface SuccessionBlock { type: string; cases: SuccessionCase[]; }

export const SUCCESSION_CASES: SuccessionBlock[] = [
  {
    type: 'Successor CEO',
    cases: [
      { n: 'Case 1', body: 'Engineering veteran with corporate-planning experience appointed as Representative Director.' },
      { n: 'Case 2', body: 'Corporate-management hire appointed as Director, overseeing the administration division.' },
    ],
  },
  {
    type: 'Next-gen leader development',
    cases: [
      { n: 'Case 1', body: 'Restructured management with founding-family successor stepping up to Representative Director.' },
      { n: 'Case 2', body: 'Existing managers coached and developed into the next executive cohort.' },
    ],
  },
];

export const BACK_OFFICE: string[] = ['Accounting', 'Legal', 'General affairs', 'IT systems', 'HR'];
